"use client"

import { Button } from "@/components/ui/button"
import { useState, useEffect } from "react"
import {
    flexRender,
    getCoreRowModel,
    getPaginationRowModel,
    useReactTable,
    getSortedRowModel,
    getFilteredRowModel,
    filterFns,
} from "@tanstack/react-table"

import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table"

import { useMemo, memo } from "react";

const filterByNameOrID = (row, columnId, filterValue) => {
    if (parseInt(filterValue) != NaN && columnId == "id") {
        const id = row.getValue('id');
        return id == parseInt(filterValue);
    }

    return filterFns.includesString(row, columnId, filterValue);
}

import { TableStateContext } from '@/components/table/dataTableStateContext';

export const DataTable = memo(function DataTable({ pageSize, columns, data, loadAllItems, globalFilter, setGlobalFilter, favorites, pinned, setFavorites, setPinned }) {
    const [sorting, setSorting] = useState([]);
    const [columnFilters, setColumnFilters] = useState([]);
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: pageSize,
    });

    useEffect(() => {
        loadAllItems();
    }, []);

    data = data.map(item => {
        return {
            ...item,
            favorited: favorites.has(item.id),
            pinned: pinned.has(item.id),
        }
    })

    const [rowPinning, setRowPinning] = useState({
        top: Array.from(pinned, ([key, value]) => key),
    });

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        onSortingChange: setSorting,
        getSortedRowModel: getSortedRowModel(),
        onColumnFiltersChange: setColumnFilters,
        getFilteredRowModel: getFilteredRowModel(),
        onPaginationChange: setPagination,
        state: {
            sorting,
            columnFilters,
            pagination,
            globalFilter: globalFilter,
            rowPinning,
        },
        onGlobalFilterChange: setGlobalFilter,
        globalFilterFn: filterByNameOrID,
        autoResetPageIndex: false, // turn off page index reset when sorting or filtering or updating data
        onRowPinningChange: setRowPinning,
    });

    return (
        <TableStateContext.Provider value={{ favorites, setFavorites: setFavorites, pinned, setPinned: setPinned }}>
            <div className="rounded-md border">
                <Table>
                    <TableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <TableHead key={header.id}>
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(
                                                header.column.columnDef.header,
                                                header.getContext()
                                            )}
                                    </TableHead>
                                ))}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getState().rowPinning.top
                            .filter(id => table.getRowModel().rowsById[id])
                            .map(id => {
                                const row = table.getRowModel().rowsById[id];
                                return (
                                    <TableRow key={row.id} data-state="pinned">
                                        {row.getVisibleCells().map(cell => (
                                            <TableCell key={cell.id}>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                );
                            })}

                        {table.getRowModel().rows.length != 0 ?
                            table.getRowModel().rows.map((row) => {
                                if (row.getIsPinned()) {
                                    return null;
                                }
                                return <TableRow key={row.id} data-state={row.getIsSelected() && "selected"}
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                                    ))}
                                </TableRow>
                            })
                         :
                            <TableRow>
                                <TableCell colSpan={columns.length} className="h-24 text-center">
                                    No results.
                                </TableCell>
                            </TableRow>
                        }
                    </TableBody>
                </Table>
            </div>
            <div className="flex items-center justify-center space-x-2 py-4">
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => table.previousPage()}
                    disabled={!table.getCanPreviousPage()}
                >
                    Previous
                </Button>
                <span className="text-sm">
                    Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
                </span>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => table.nextPage()}
                    disabled={!table.getCanNextPage()}
                >
                    Next
                </Button>
            </div>
        </TableStateContext.Provider>
    );
})